import { ComponentType } from 'react';
import { useModalLayer, LayerDepthContext, type ModalLayer } from './layerManager';

interface BackNavigableProps {
    isOpen: boolean;
    onClose: () => void;
    layer?: ModalLayer;
}

/**
 * HOC that adds browser/native back-button support and automatic z-index layering
 * to any modal component. The wrapped component receives `onClose` (history-aware)
 * and `layer` (with depth, zIndex, style) via props.
 */
export function withBackNavigation<P extends BackNavigableProps>(
    WrappedComponent: ComponentType<P>,
): ComponentType<Omit<P, 'layer'>> {
    function WithBackNav(props: Omit<P, 'layer'>) {
        const layer = useModalLayer({
            enabled: (props as BackNavigableProps).isOpen,
            onClose: (props as BackNavigableProps).onClose,
        });
        return (
            <LayerDepthContext.Provider value={layer.depth}>
                <WrappedComponent {...props as P} onClose={layer.onClose} layer={layer} />
            </LayerDepthContext.Provider>
        );
    }
    WithBackNav.displayName = `WithBackNav(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
    return WithBackNav;
}
